import { useMemo } from 'react';

import {
  CoreDocumentsState,
  DocumentFields,
  DocumentsSections,
  coreDocumentsDefaultState,
  deleteDocument,
  fetchDocument,
  loadDocuments,
  resetDocuments,
  saveDocument,
} from '@cd3p/core/modules/documents';
import { APILanguage, APISortOrder } from '@cd3p/core/types/api';

import {
  bindActionCreators,
  simplifyBuilder,
  useDispatch,
  useSelector,
} from 'third-party';

import { appSelectors, releaseNotesSelectors } from 'selectors';

export type ReleaseNotesState = CoreDocumentsState & {
  loadReleaseNotesDocumentsPending: boolean;
  fetchReleaseNotesDocumentPending: boolean;
};

const releaseNotesDefaultState: ReleaseNotesState = {
  ...coreDocumentsDefaultState,
  loadReleaseNotesDocumentsPending: false,
  fetchReleaseNotesDocumentPending: false,
  searchSortOrders: [
    {
      sortField: DocumentFields.PublishedDate,
      sortOrder: APISortOrder.DESC,
    },
  ],
};

const builder = simplifyBuilder(releaseNotesDefaultState, {});

export const loadReleaseNotesDocuments = builder.createServerAction(
  loadDocuments('loadReleaseNotesDocuments'),
);

export const fetchReleaseNotesDocument = builder.createServerAction(
  fetchDocument('fetchReleaseNotesDocument'),
);

export const saveReleaseNotesDocument = builder.createServerAction(
  saveDocument('saveReleaseNotesDocument'),
);

export const deleteReleaseNotesDocument = builder.createServerAction(
  deleteDocument('deleteReleaseNotesDocument'),
);

export const resetReleaseNotesDocuments = builder.createReduxAction(
  resetDocuments('resetReleaseNotesDocuments'),
);

export const useReleaseNotes = (
  sectionId = DocumentsSections.WhatIsNewWebDispatcher,
  language = APILanguage.English,
) => {
  const dispatch = useDispatch();
  const providerId = useSelector(appSelectors.providerId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          fetchReleaseNotesDocument: fetchReleaseNotesDocument.bind(
            null,
            providerId,
            sectionId,
          ),
          saveReleaseNotesDocument: saveReleaseNotesDocument.bind(
            null,
            providerId,
            sectionId,
            language,
          ),
          deleteReleaseNotesDocument: deleteReleaseNotesDocument.bind(
            null,
            providerId,
            sectionId || '',
          ),
          loadReleaseNotesDocuments: loadReleaseNotesDocuments.bind(
            null,
            providerId,
            sectionId,
            language,
            releaseNotesSelectors.releaseNotes,
          ),
          resetReleaseNotesDocuments: resetReleaseNotesDocuments.bind(null),
        },
        dispatch,
      ),
    [dispatch, providerId, sectionId, language],
  );
};

export const releaseNotesReducer = builder.getReducers();
